<template>
  <a-modal
    class="license-add-modal"
    :title="$t('LC_ModalTitle')"
    :visible="visible"
    :maskClosable="false"
    centered
    @cancel="handleCancel"
  >
    <!-- custom footer -->
    <template v-slot:footer>
      <a-button key="back" @click="handleCancel">{{$t("Common_Cancel")}}</a-button>
      <a-button
        key="submit"
        :loading="loading"
        @click="handleOk"
        type="primary"
        :class="{ disable: invalidCount }"
      >
        {{$t("Header_Issue")}}
      </a-button>
    </template>

    <!-- contents -->
    <div class="content-text">
      <div>{{$t("LC_ModalContext1")}}</div>
    </div>
    <div class="content-group">
      <div class="require-text">{{$t("LC_ModalContext2")}}</div>
      <div class="content-item">
        <div class="modal-title">{{$t("LC_Modal_Client")}}</div>
        <div class="modal-value">
          <input class="modal-input" type="text" v-model="customer" />
        </div>
      </div>
      <div class="content-item">
        <div class="modal-title">{{$t("LC_Modal_ServiceNum")}}</div>
        <div class="modal-value">
          <input class="modal-input" type="text" v-model="serviceNo" />
        </div>
      </div>

      <div class="content-item">
        <div class="modal-title">{{$t("LC_Modal_LicenseType")}}</div>
        <div class="modal-value">
          <a-select
            :value="licenseType"
            @change="(value) => onDropdownSelect('licenseType', value)"
          >
            <a-select-option
              v-for="(item, index) in licenseList"
              :key="index"
              :value="item.value"
              >{{ item.text }}
            </a-select-option>
          </a-select>
        </div>
      </div>

      <div v-if="licenseType === 4" class="content-item">
        <div class="modal-title">{{$t("Common_TrainingNum")}}</div>
        <div class="modal-value">
          <a-select
            :value="examLimitCount"
            @change="(value) => onDropdownSelect('examLimitCount', value)"
          >
            <a-select-option
              v-for="(item, index) in examLimitCountList"
              :key="index"
              :value="item.value"
              >{{ item.text }}
            </a-select-option>
          </a-select>
        </div>
      </div>

      <div v-if="licenseType === 2" class="date-area">
        <div class="content-item">
          <div class="modal-title">{{$t("LC_Modal_StartDate")}}</div>
          <div class="modal-value">
            <a-date-picker
              v-model="startDate"
              :value="startDate"
              @change="(date) => onDateChange('startDate', date)"
              :placeholder="$t('LC_Modal_DateSelect_Text')"
              :allowClear="false"
              :disabledDate="
                (current) => handleDisableDate('startDate', current)
              "
            >
              <template v-slot:dateRender="{ current }">
                <div
                  class="ant-calendar-date"
                  :class="{
                    active: current >= startDate && current <= endDate,
                  }"
                >
                  {{ current.date() }}
                </div>
              </template>
            </a-date-picker>
          </div>
        </div>
        <div class="content-item">
          <div class="modal-title">{{$t("LC_Modal_EndDate")}}</div>
          <div class="modal-value">
            <a-date-picker
              v-model="endDate"
              :value="endDate"
              @change="(date) => onDateChange('endDate', date)"
              :placeholder="$t('LC_Modal_DateSelect_Text')"
              :allowClear="false"
              :disabledDate="(current) => handleDisableDate('endDate', current)"
            >
              <template v-slot:dateRender="{ current }">
                <div
                  class="ant-calendar-date"
                  :class="{
                    active: current >= startDate && current <= endDate,
                  }"
                >
                  {{ current.date() }}
                </div>
              </template>
            </a-date-picker>
          </div>
          <div class="date-period">
            <div class="date-text">
              {{$t("LC_Modal_ValDate_Text1")}}:
              {{
                endDate && startDate
                  ? Math.round(
                      moment.duration(endDate.diff(startDate)).asDays()
                    )
                  : 0
              }}{{$t("LC_Modal_ValDate_Text2")}}
            </div>
            <div class="date-button" @click="adjustEndDateOneYear">
              {{$t("LC_Modal_ValDate_Text3")}}
            </div>
          </div>
        </div>
      </div>

      <div class="content-item">
        <div class="modal-title">{{$t("LC_Modal_LicenseQuantity")}}</div>
        <div class="modal-value">
          <input class="modal-input" type="text" v-model="licenseCount" />
        </div>
      </div>

      <div class="content-item">
        <div class="modal-title">blkid</div>
        <div class="modal-value">
          <input class="modal-input" type="text" v-model="blkid" />
        </div>
      </div>

      <div class="content-item">
        <div class="modal-title">{{$t("Common_Content")}}</div>
        <div class="modal-value">
          <input class="modal-input" type="text" v-model="licenseContent" />
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { toRefs, ref, reactive, onMounted, watch } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import fileDownload from "js-file-download";
import moment from "moment";
// import "moment/locale/ko";
import { useI18n } from "vue-i18n";

import { apiUrl } from "@/utils/common";

// moment.locale("ko");

export default {
  name: "LicenseAddModal",
  props: {
    visible: Boolean,
    toggleModal: Function,
  },
  setup(props) {
    const store = useStore();
    const { t } = useI18n();

    const state = reactive({
      loading: false,
      customer: "",
      serviceNo: null,
      licenseType: null,
      examLimitCount: null,
      startDate: moment().hour(0).minute(0).second(0),
      endDate: moment()
        .hour(0)
        .minute(0)
        .second(0)
        .add(1, "y")
        .subtract(1, "s"),
      licenseCount: null,
      blkid: "",
      licenseContent: "",
      invalidCount: 0,
      enable: true,
    });

    const licenseList = [
      {
        value: 1,
        text: t("LC_Modal_LicenseList1"),
      },
      {
        value: 2,
        text: t("LC_Modal_LicenseList2"),
      },
      {
        value: 4,
        text: t("LC_Modal_LicenseList3"),
      },
    ];

    const examLimitCountList = [
      {
        value: 1,
        text: "1 "+ t("LC_Modal_Times"),
      },
      {
        value: 2,
        text: "2 "+ t("LC_Modal_Times"),
      },
      {
        value: 3,
        text: "3 "+ t("LC_Modal_Times"),
      },
      {
        value: 4,
        text: "4 "+ t("LC_Modal_Times"),
      },
      {
        value: 5,
        text: "5 "+ t("LC_Modal_Times"),
      },
      {
        value: 10,
        text: "10 "+ t("LC_Modal_Times"),
      },
      {
        value: 20,
        text: "20 "+ t("LC_Modal_Times"),
      },
      {
        value: 30,
        text: "30 "+ t("LC_Modal_Times"),
      },
      {
        value: 40,
        text: "40 "+ t("LC_Modal_Times"),
      },
      {
        value: 50,
        text: "50 "+ t("LC_Modal_Times"),
      },
      {
        value: 100,
        text: "100 "+ t("LC_Modal_Times"),
      },
    ];

    // 드롭다운 선택 이벤트
    const onDropdownSelect = (name, value) => {
      state[name] = value;
    };

    // 유효기간 변경 이벤트
    const onDateChange = (name, date) => {
      state[name] = date;

      if (state.startDate > state.endDate) {
        state["endDate"] = null;
      }
    };

    // 1년으로 종료일 맞춤
    const adjustEndDateOneYear = () => {
      state.endDate = moment(state.startDate)
        .hour(0)
        .minute(0)
        .second(0)
        .add(1, "y")
        .subtract(1, "s");
    };

    // 유효기간 시작/종료일자 선택 불가 범위 지정
    const handleDisableDate = (name, current) => {
      const todayMoment = moment().hour(0).minute(0).second(0);
      if (name === "startDate") {
        return current < todayMoment || current > moment("2036-12-31");
      } else if (name === "endDate") {
        return current < state.startDate || current > moment("2037-12-31");
      }
    };

    // 필수 항목 입력 여부 체크
    const checkValid = (compareArray) => {
      let enableArray = [];
      compareArray.map((key) => {
        const value = state[key];
        if (!!value || value === 0) {
          enableArray.push(key);
        }
      });

      state["enable"] = enableArray.length === compareArray.length;
    };

    const handleOk = async () => {
      state.loading = true;

      const params = {
        customer: state.customer,
        serviceNo: parseInt(state.serviceNo),
        licenseType: state.licenseType,
        licenseCount: parseInt(state.licenseCount),
        blkid: state.blkid,
        licenseContent: state.licenseContent,
      };
      const targetArray = Object.keys(params);

      if (state.licenseType === 4) {
        targetArray.push("examLimitCount");
        checkValid(targetArray);
      } else {
        checkValid(targetArray);
      }

      if (state.licenseType === 1) {
        // 영구 라이센스
        params["startEpoch"] = moment().hour(0).minute(0).second(0).unix();
      } else if (state.licenseType === 2) {
        // 기간 라이센스
        params["startEpoch"] = state.startDate
          .hour(0)
          .minute(0)
          .second(0)
          .unix();
        params["endEpoch"] = state.endDate
          .hour(23)
          .minute(59)
          .second(59)
          .unix();
      } else if (state.licenseType === 4) {
        // 횟수 라이센스
        params["examLimitCount"] = state.examLimitCount;
      }

      if (!state.invalidCount && state["enable"]) {
        try {
          const response = await axios({
            method: "post",
            headers: {
              "Content-Type": "application/octet-stream",
            },
            responseType: "arraybuffer",
            url: apiUrl + "License",
            data: params,
          });
          if (response && !response.data.error) {
            store.dispatch("license/ADD_LICENSE", response);
            fileDownload(response.data, "mudfix_license.enc");
          }
        } catch (error) {
          console.log("error", error);
        }
        props.toggleModal();
      }

      state.loading = false;
    };

    const handleCancel = () => {
      props.toggleModal();
    };

    return {
      ...toRefs(state),
      moment,
      licenseList,
      examLimitCountList,
      handleOk,
      handleCancel,
      onDropdownSelect,
      onDateChange,
      adjustEndDateOneYear,
      handleDisableDate,
    };
  },
};
</script>